/**
 * What the voice sample's screens derive from its numbers (ADR-0062): the clock in the ring, how
 * far the ring is drawn, the ring's look in each phase and what a screen reader is told. Pure.
 */
import {
  isEnoughSpeech,
  isTargetReached,
  VOICE_ROOM_SAID_MS,
  VOICE_SAMPLE_TARGET_SPEECH_MS,
  VOICE_SILENCE_ANNOUNCE_MS,
} from './voice-rules'
import type { VoiceSampleQuality } from './voice-sample-quality'
import type {
  VoiceAnnounced,
  VoiceCounting,
  VoiceGuidance,
  VoiceReadout,
  VoiceRoom,
  VoiceSampleView,
  VoiceTakeSummary,
} from './voice-types'

/** Whole seconds of a duration; never rounded up, so a count reads 30 only once it is reached. */
export const wholeSeconds = (ms: number): number => Math.floor(Math.max(0, ms) / 1000)

/** A duration as the ring shows it: 18 400 → "0:18", 75 000 → "1:15". */
export function formatSpeechClock(ms: number): string {
  const total = wholeSeconds(ms)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/** How far the ring is drawn, 0..1. */
export function speechShare(
  speechMs: number,
  targetMs: number = VOICE_SAMPLE_TARGET_SPEECH_MS
): number {
  if (targetMs <= 0) return 0
  return Math.min(1, Math.max(0, speechMs / targetMs))
}

/**
 * The ring's look: `idle` before a recording, `speaking` while a voice is counted, `waiting`
 * while none is heard (the stroke dims), `kept` for a recorded sample, `reached` for one that
 * holds all the speech asked for (the ring closes with its seal), `short` for one with too little
 * speech (the warning tone), `weak` for a stored sample the server graded low (the warning tone,
 * and no seal however much speech it holds).
 */
export type SpeechRingState =
  | 'idle'
  | 'speaking'
  | 'waiting'
  | 'kept'
  | 'reached'
  | 'short'
  | 'weak'

/** What the ring shows in a phase. */
export interface SpeechRingReading {
  /** The speech counted; null where the count made here did not hold. */
  speechMs: number | null
  /** How it was counted: the ring's words say *recorded*, not *speech*, where it was time. */
  counting: VoiceCounting
  state: SpeechRingState
}

/**
 * The ring of a stored sample the server has judged: the speech the server measured stands in
 * place of the count made here, never past what was asked for, and a sample graded low carries
 * the warning tone and no seal. Where the server names no measure the count made here stays.
 */
function judgedRing(take: VoiceTakeSummary, quality: VoiceSampleQuality): SpeechRingReading {
  const measured = quality.speechMs !== null
  const speechMs = Math.min(quality.speechMs ?? take.speechMs, VOICE_SAMPLE_TARGET_SPEECH_MS)
  const counting = measured ? 'speech' : take.counting
  if (quality.grade === 'low') return { speechMs, counting, state: 'weak' }
  const reached = counting === 'speech' && isTargetReached(speechMs)
  return { speechMs, counting, state: reached ? 'reached' : 'kept' }
}

/**
 * The ring never says more than the phase does. The server measures the audio itself: where it
 * found too little speech, the count made here did not hold and none is shown; where it judged
 * the sample, its own measure is shown. The seal is for a sample that stands with all the speech
 * asked for — counted as speech, not as time, not one that failed on its way, and not one the
 * server found weak. While the room is checked nothing is counted yet.
 */
export function ringOf(view: VoiceSampleView, readout: VoiceReadout): SpeechRingReading {
  if (view.phase === 'instructions' || view.phase === 'room') {
    return { speechMs: 0, counting: 'speech', state: 'idle' }
  }
  if (view.phase === 'recording') {
    const { speechMs, counting, speaking } = readout
    return { speechMs, counting, state: speaking ? 'speaking' : 'waiting' }
  }
  if (view.phase === 'done' && view.quality) return judgedRing(view.take, view.quality)
  const { speechMs, counting } = view.take
  if (view.phase === 'no_speech') return { speechMs: null, counting, state: 'short' }
  if (!isEnoughSpeech(speechMs)) return { speechMs, counting, state: 'short' }
  const reached = counting === 'speech' && isTargetReached(speechMs) && view.phase !== 'failed'
  return { speechMs, counting, state: reached ? 'reached' : 'kept' }
}

/**
 * What the caption under the ring says while it records: of the voice, what to ask of it, or —
 * at the start — what the room check found.
 */
export type VoiceLiveLine = VoiceAnnounced | VoiceRoom

/**
 * The caption the operator sees: it follows the voice at once. What to ask of the voice comes
 * first — too faint, or clipping — for as long as it holds. At the start, until a voice is heard
 * or a few seconds have passed (`VOICE_ROOM_SAID_MS`), it says what the room check found in
 * place of *waiting for a voice*.
 */
export function liveCaption(readout: VoiceReadout, room: VoiceRoom | null = null): VoiceLiveLine {
  if (readout.guidance) return readout.guidance
  if (readout.counting === 'time') return 'time'
  if (readout.speaking) return 'speaking'
  const justChecked = readout.speechMs === 0 && readout.talkMs < VOICE_ROOM_SAID_MS
  return room && justChecked ? room : 'waiting'
}

/** What the speech meter says of the voice at one tick. */
export interface VoiceHearing {
  speaking: boolean
  /** For how long no voice has been heard; 0 while one is. */
  silentMs: number
  counting: VoiceCounting
  /** What is asked of the voice now, where a screen reader is to be told; else null or absent. */
  guidance?: VoiceGuidance | null
}

/**
 * What a screen reader is told of the voice after one more tick, from what it was told last. A
 * silence is told only once it has lasted (`VOICE_SILENCE_ANNOUNCE_MS`), and a voice only when it
 * is new: the pauses of ordinary speech change nothing, so nothing talks over the subject. What
 * to ask of the voice is told in place of both; once it has passed, it stands until the voice
 * gives something new to tell.
 */
export function nextAnnouncement(
  previous: VoiceAnnounced | null,
  hearing: VoiceHearing
): VoiceAnnounced | null {
  if (hearing.guidance) return hearing.guidance
  if (hearing.counting === 'time') return 'time'
  if (hearing.speaking) return 'speaking'
  return hearing.silentMs >= VOICE_SILENCE_ANNOUNCE_MS ? 'waiting' : previous
}
